const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;
const GTM_CONTAINER_ID = import.meta.env.VITE_GTM_CONTAINER_ID;
const isProduction = import.meta.env.PROD;

const BLOCKED_PARAM_KEYS = [
  'email',
  'phone',
  'whatsapp',
  'cpf',
  'birth_date',
  'birthdate',
  'address',
  'pin',
  'code',
  'token',
  'title',
  'body',
  'free_text',
  'search_term',
  'question',
  'url',
  'doi',
  'source_url',
  'source_doi',
  'evidence_text',
  'message',
];

const EMAIL_PATTERN = /[^@\s]+@[^@\s]+\.[^@\s]+/;
const PHONE_PATTERN = /(?:\+?\d[\s().-]*){10,}/;

const hasAnalyticsConfig = Boolean(GA_MEASUREMENT_ID || GTM_CONTAINER_ID);

const getDataLayer = () => {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const isBlockedKey = (key) => {
  const normalizedKey = String(key || '').toLowerCase();
  return BLOCKED_PARAM_KEYS.includes(normalizedKey);
};

const getSafeUrlParts = () => {
  const url = new URL(window.location.href);
  const safeSearchParams = new URLSearchParams();
  const allowedParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'plano', 'mode', 'modo'];

  allowedParams.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value && !EMAIL_PATTERN.test(value) && !PHONE_PATTERN.test(value)) {
      safeSearchParams.set(key, value.slice(0, 100));
    }
  });

  const queryString = safeSearchParams.toString();
  const safeSearch = queryString ? `?${queryString}` : '';

  return {
    pagePath: `${url.pathname}${safeSearch}`,
    pageLocation: `${url.origin}${url.pathname}${safeSearch}`,
  };
};

const sanitizeValue = (value) => {
  if (value == null) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(sanitizeValue).filter((item) => item != null);

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (EMAIL_PATTERN.test(trimmedValue) || PHONE_PATTERN.test(trimmedValue)) return undefined;
    return trimmedValue.slice(0, 100);
  }

  return undefined;
};

/**
 * Envia eventos de analytics com sanitizacao defensiva de PII.
 *
 * Regra de negocio:
 * - Eventos devem representar decisoes de produto, nunca dados pessoais.
 * - Termos livres, contatos, PINs, URLs e evidencias nao devem sair para GA4/GTM.
 *
 * @param {string} eventName - Nome em snake_case.
 * @param {object} params - Parametros controlados do evento.
 * @returns {void}
 */
export const trackEvent = (eventName, params = {}) => {
  if (!hasAnalyticsConfig || typeof window === 'undefined' || !eventName) return;

  const safeParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (isBlockedKey(key)) return acc;

    const safeValue = sanitizeValue(value);
    if (safeValue !== undefined) acc[key] = safeValue;
    return acc;
  }, {});

  const eventPayload = {
    page_path: window.location.pathname,
    page_title: document.title,
    debug_mode: !isProduction,
    ...safeParams,
  };

  const dataLayer = getDataLayer();
  dataLayer?.push({ event: eventName, ...eventPayload });

  if (!GTM_CONTAINER_ID && typeof window.gtag === 'function' && GA_MEASUREMENT_ID) {
    window.gtag('event', eventName, eventPayload);
  }
};

/**
 * Atualiza o consentimento do Google Consent Mode.
 *
 * @param {boolean} granted - Indica se analytics_storage deve ser concedido.
 * @returns {void}
 */
export const updateAnalyticsConsent = (granted) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

export const getAnalyticsPagePayload = () => {
  const { pagePath, pageLocation } = getSafeUrlParts();

  return {
    page_path: pagePath,
    page_location: pageLocation,
    page_title: document.title,
  };
};
